import noop from 'lodash/noop'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useLocation } from 'react-router'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'

import { initializeApp } from 'firebase/app'
import { Analytics, logEvent, initializeAnalytics } from 'firebase/analytics'
import { FIREBASE_API_KEY, DEV_MODE } from 'config'
import { PAGE_VIEW_EVENT } from './events'

type EventParamKey =
  | 'page_location'
  | 'agree'
  | 'provider'
  | 'action'
  | 'risk'
  | 'asset'
  | 'collateral_asset'
  | 'deposited_amount'
  | 'amount_percents'
  | 'weth_balance'
  | 'amount'
  | 'loan_id'
  | 'address'
  | 'status'
  | 'collateral_amount'

type EventParams = Partial<Record<EventParamKey, string | undefined>>

type ContextValue = {
  trackEvent: (eventName: string, eventParams?: EventParams) => void
}

export const FirebaseContext = createContext<ContextValue>({
  trackEvent: noop,
})

type Props = {
  children?: React.ReactNode
}

export const FirebaseProvider = ({ children }: Props) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const { walletAddress } = useWeb3()
  const { pathname } = useLocation()
  const { tokenId, isPrimaryAddress } = useNFCSState()

  useEffect(() => {
    if (!DEV_MODE && !analytics && FIREBASE_API_KEY) {
      const app = initializeApp({
        apiKey: FIREBASE_API_KEY,
        authDomain: 'roci-web.firebaseapp.com',
        projectId: 'roci-web',
        storageBucket: 'roci-web.appspot.com',
        messagingSenderId: '743105056289',
        appId: '1:743105056289:web:9369161864dd756bd01d60',
      })
      setAnalytics(
        initializeAnalytics(app, {
          config: { send_page_view: false },
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackEvent = useCallback(
    (eventName, eventParams?: EventParams) => {
      if (DEV_MODE) {
        console.log(eventName, {
          wallet_address: walletAddress?.substr(1),
          nfcs_id: tokenId,
          v1: 'true',
          ...eventParams,
        })
        return
      }
      if (!analytics) {
        return
      }
      logEvent(analytics, eventName, {
        wallet_address: walletAddress?.substr(1),
        nfcs_id: tokenId,
        is_primary_nfcs_address: isPrimaryAddress ? 'true' : 'false',
        ...eventParams,
      })
    },
    [analytics, walletAddress, tokenId, isPrimaryAddress],
  )

  useEffect(() => {
    trackEvent(PAGE_VIEW_EVENT, { page_location: window.location.href })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <FirebaseContext.Provider value={{ trackEvent }}>
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebase = () => useContext(FirebaseContext)
